import {
  MapPin, Home, Bed, Bath, Ruler, Sparkles, AlertTriangle, ListChecks,
  TrendingUp, Building2, BarChart3, LineChart as LineIcon, Target,
} from "lucide-react";
import { useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  AreaChart, Area, ScatterChart, Scatter, ZAxis, Cell, ReferenceDot,
} from "recharts";
import { fmtMoney, fmtPct } from "@/lib/api";
import { Img } from "@/components/Img";
import PhotoLightbox from "@/components/PhotoLightbox";
import { usePropertyPhotos } from "@/lib/usePropertyPhotos";
import RehabScopeCard from "@/components/RehabScopeCard";
import AIReportSections from "@/components/AIReportSections";

const VERDICT_STYLE = {
  GO: { bg: "rgba(16,185,129,0.12)", color: "#047857", border: "rgba(16,185,129,0.35)" },
  MAYBE: { bg: "rgba(245,158,11,0.12)", color: "#B45309", border: "rgba(245,158,11,0.35)" },
  PASS: { bg: "rgba(231,111,81,0.14)", color: "#B91C1C", border: "rgba(231,111,81,0.35)" },
};

const HERO_IMG = "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3OTQ3ODQ2MHww&ixlib=rb-4.1.0&q=85";

export function ReportBody({ lead, report, onPhotoView, engagement, photoEndpoint, authToken }) {
  const m = report.metrics || {};
  const verdict = report.ai_verdict || lead.ai_verdict;
  const vstyle = VERDICT_STYLE[verdict] || {};
  const agent = report.agent || {};
  const comps = report.comps;
  const photos = (lead.photos && lead.photos.length > 0)
    ? lead.photos
    : (lead.photo_url ? [lead.photo_url] : []);  // backwards compat for any un-migrated docs
  const { streetUrl, satelliteUrl } = usePropertyPhotos({ endpointBase: photoEndpoint, authToken });
  // Hero photo priority: user-uploaded > Google Street View
  const heroPhoto = photos[0] || streetUrl || null;
  const [lightboxIdx, setLightboxIdx] = useState(null);  // index | null

  return (
    <article className="bg-white print:bg-white" data-testid="report-body">
      {/* Header */}
      <header className="rounded-2xl overflow-hidden mb-6 relative print:break-inside-avoid" style={{ background: "var(--ink)", color: "#fff" }}>
        <div
          className={"absolute inset-0 bg-cover bg-center " + (heroPhoto ? "cursor-zoom-in" : "")}
          style={{
            backgroundImage: `url('${heroPhoto || HERO_IMG}')`,
            opacity: heroPhoto ? 0.55 : 0.3,
          }}
          onClick={() => { if (heroPhoto) setLightboxIdx(0); }}
          data-testid="report-hero-photo"
        />
        <div className="absolute inset-0" style={{ background: heroPhoto
          ? "linear-gradient(115deg, rgba(11,19,43,0.92) 25%, rgba(11,19,43,0.45) 100%)"
          : "linear-gradient(115deg, rgba(11,19,43,0.95) 30%, rgba(11,19,43,0.65) 100%)" }} />
        <div className="relative p-8 lg:p-12 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-[var(--primary)]/20 text-[#A7F3D0] border border-[var(--primary)]/40">
                <Sparkles size={11} /> Property Analysis Report
              </span>
              {verdict && (
                <span className="chip" style={{ background: "rgba(16,185,129,0.18)", color: "#A7F3D0", borderColor: "rgba(16,185,129,0.4)", fontWeight: 700 }}>
                  <span className="chip-dot" style={{ background: vstyle.color || "var(--primary)" }} /> {verdict}
                </span>
              )}
            </div>
            <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter leading-[1.05]">
              {lead.property_address}
            </h1>
            <div className="text-white/65 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="flex items-center gap-1"><MapPin size={13} /> {[lead.city, lead.state, lead.zip_code].filter(Boolean).join(", ") || "—"}</span>
              {lead.bedrooms ? <span className="flex items-center gap-1"><Bed size={13} /> {lead.bedrooms} bed</span> : null}
              {lead.bathrooms ? <span className="flex items-center gap-1"><Bath size={13} /> {lead.bathrooms} bath</span> : null}
              {lead.sqft ? <span className="flex items-center gap-1"><Ruler size={13} /> {Number(lead.sqft).toLocaleString()} sqft</span> : null}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 self-center">
            <Mini label="ARV" value={fmtMoney(lead.estimated_arv)} />
            <Mini label="Asking" value={fmtMoney(lead.asking_price)} />
            <Mini label="Repairs" value={fmtMoney(lead.estimated_repairs)} />
            <Mini label="MAO (70%)" value={fmtMoney(m.wholesale?.mao)} accent />
          </div>
        </div>
      </header>

      {/* Executive summary */}
      {report.ai_summary && (
        <section className="card mb-6 print:break-inside-avoid">
          <div className="label-eyebrow mb-2">Executive Summary</div>
          <p className="text-[15px] leading-relaxed">{report.ai_summary}</p>
        </section>
      )}

      {/* AI Deal Intelligence + AI Valuation — surfaces the Pro-tier AI
         analysis directly inside the shareable report so buyers/JV
         partners see the score + MAO without needing app access. */}
      <AIReportSections lead={lead} />

      {/* Photo gallery — only when more than one photo (hero already in header) */}
      {photos.length > 1 && (
        <section className="card mb-6 print:break-inside-avoid" data-testid="report-photo-gallery">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="label-eyebrow">Property Photos</div>
            {engagement && engagement.total_views > 0 && (
              <div
                className="text-xs text-[var(--ink-2)] flex items-center gap-1.5 print:hidden"
                data-testid="report-photo-engagement"
                title={engagement.last_view_at ? `Last viewed: ${new Date(engagement.last_view_at).toLocaleString()}` : ""}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <strong className="text-[var(--ink)] tabular-nums">{engagement.total_views}</strong>
                <span>view{engagement.total_views === 1 ? "" : "s"}</span>
                <span className="text-[var(--ink-3)]">·</span>
                <strong className="text-[var(--ink)] tabular-nums">{engagement.unique_sessions}</strong>
                <span>visitor{engagement.unique_sessions === 1 ? "" : "s"}</span>
                {engagement.avg_photos_seen_per_session > 0 && (
                  <>
                    <span className="text-[var(--ink-3)]">·</span>
                    <span>avg {engagement.avg_photos_seen_per_session} / session</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Per-buyer attribution strip */}
          {engagement?.by_buyer?.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2 print:hidden" data-testid="report-buyer-engagement">
              {engagement.by_buyer.map((b) => (
                <div
                  key={b.team_member_id}
                  className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 bg-violet-50/50"
                  style={{ borderColor: "#ddd6fe" }}
                  data-testid={`report-buyer-row-${b.team_member_id}`}
                  title={b.last_view_at ? new Date(b.last_view_at).toLocaleString() : ""}
                >
                  <span className="w-6 h-6 rounded-full bg-violet-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                    {(b.name || "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[var(--ink)] truncate max-w-[150px]">{b.name}</div>
                    <div className="text-[10px] text-[var(--ink-2)]">
                      <strong>{b.photos_seen}</strong> of {photos.length} photos
                      {b.views !== b.photos_seen && <span> · {b.views} opens</span>}
                      {b.last_view_at && <span> · {relTime(b.last_view_at)}</span>}
                    </div>
                  </div>
                </div>
              ))}
              {engagement.anonymous_views > 0 && (
                <div
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 bg-slate-50"
                  title="Photo views from non-attributed share links"
                >
                  <span className="w-6 h-6 rounded-full bg-slate-400 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                    ?
                  </span>
                  <div className="text-[10px] text-[var(--ink-2)]">
                    <strong>{engagement.anonymous_views}</strong> anonymous view{engagement.anonymous_views === 1 ? "" : "s"}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((url, idx) => {
              const photoStats = engagement?.by_photo?.[idx];
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIdx(idx)}
                  className="block rounded-lg overflow-hidden border border-[var(--line)] aspect-[4/3] group relative cursor-zoom-in print:break-inside-avoid"
                  data-testid={`report-photo-${idx}`}
                  aria-label={`Open photo ${idx + 1} of ${photos.length}`}
                >
                  <img
                    src={url}
                    alt={`Property photo ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  {idx === 0 && (
                    <span className="absolute top-2 left-2 bg-[var(--primary)] text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                      Hero
                    </span>
                  )}
                  {photoStats && photoStats.views > 0 && (
                    <span
                      className="absolute top-2 right-2 bg-black/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded backdrop-blur-sm print:hidden"
                      title={`Opened ${photoStats.views} time${photoStats.views === 1 ? "" : "s"}`}
                    >
                      👁 {photoStats.views}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {lightboxIdx !== null && (
        <PhotoLightbox
          photos={photos}
          index={lightboxIdx}
          onChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPhotoView={onPhotoView}
        />
      )}

      {/* 4-strategy grid */}
      {/* AI-verified rehab scope — only renders when an AI scan exists on the lead. */}
      {lead.rehab_estimate && (
        <div className="mb-6">
          <RehabScopeCard estimate={lead.rehab_estimate} accent={agent.brand_accent || "#10B981"} />
        </div>
      )}

      <section className="grid md:grid-cols-2 gap-5 mb-6">
        <StrategyCard title="Wholesale (70% Rule)" icon="W"
          metrics={m.wholesale && [
            { label: "MAO", value: fmtMoney(m.wholesale.mao), accent: true },
            { label: "Projected Profit", value: fmtMoney(m.wholesale.projected_profit) },
            { label: "ROI", value: fmtPct(m.wholesale.roi_percent) },
            { label: "Recommendation", value: m.wholesale.recommendation, full: true },
          ]}
        />
        <StrategyCard title="Fix & Flip" icon="F"
          metrics={m.flip && [
            { label: "Purchase", value: fmtMoney(m.flip.purchase_price) },
            { label: "Total Costs", value: fmtMoney(m.flip.total_costs) },
            { label: "Projected Profit", value: fmtMoney(m.flip.projected_profit), accent: m.flip.projected_profit > 0 },
            { label: "ROI", value: fmtPct(m.flip.roi_percent), accent: true },
          ]}
        />
        <StrategyCard title="BRRRR" icon="B"
          metrics={m.brrrr && [
            { label: "Refi Loan (75% LTV)", value: fmtMoney(m.brrrr.refi_loan) },
            { label: "Cash Left In", value: fmtMoney(m.brrrr.cash_left_in_deal), accent: m.brrrr.cash_left_in_deal === 0 },
            { label: "Cash Pulled Out", value: fmtMoney(m.brrrr.cash_pulled_out) },
            { label: "Monthly Cash Flow", value: fmtMoney(m.brrrr.monthly_cash_flow), accent: m.brrrr.monthly_cash_flow > 0 },
            { label: "Est. Monthly Rent", value: fmtMoney(m.brrrr.estimated_monthly_rent) },
            { label: "Cash-on-Cash", value: m.brrrr.cash_on_cash_pct === "Infinite" ? "∞" : fmtPct(m.brrrr.cash_on_cash_pct), accent: true },
          ]}
        />
        <StrategyCard title="Rental (Buy & Hold)" icon="R"
          metrics={m.rental && [
            { label: "Purchase", value: fmtMoney(m.rental.purchase_price) },
            { label: "Down Payment (20%)", value: fmtMoney(m.rental.down_payment) },
            { label: "Monthly P&I", value: fmtMoney(m.rental.monthly_pi) },
            { label: "Monthly Cash Flow", value: fmtMoney(m.rental.monthly_cash_flow), accent: m.rental.monthly_cash_flow > 0 },
            { label: "Cap Rate", value: fmtPct(m.rental.cap_rate_pct), accent: true },
            { label: "Cash-on-Cash", value: fmtPct(m.rental.cash_on_cash_pct) },
          ]}
        />
      </section>

      {/* Charts */}
      <ChartsSection lead={lead} metrics={m} comps={comps} satelliteUrl={satelliteUrl} />

      {/* Risks + Next steps */}
      {(report.ai_risks?.length || report.ai_next_steps?.length) ? (
        <section className="grid md:grid-cols-2 gap-5 mb-6 print:break-inside-avoid">
          <ListBlock icon={AlertTriangle} title="Risks" items={report.ai_risks} accent="warn" />
          <ListBlock icon={ListChecks} title="Next steps" items={report.ai_next_steps} accent="primary" />
        </section>
      ) : null}

      {/* Comps */}
      {comps && (
        <section className="card mb-6 print:break-inside-avoid" data-testid="report-comps">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="label-eyebrow flex items-center gap-1.5"><Sparkles size={11} className="text-[var(--primary)]" /> Comps (AI-estimated)</div>
              <h2 className="font-display font-bold text-xl mt-1">Recent activity nearby</h2>
            </div>
            <span className="chip" style={{ background: "rgba(15,23,42,0.06)", borderColor: "var(--line)", color: "var(--ink-2)" }}>
              Confidence · {comps.confidence || "—"}
            </span>
          </div>
          {comps.narrative && <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-4">{comps.narrative}</p>}
          <div className="grid md:grid-cols-2 gap-5">
            <CompTable title="Sales comps" rows={comps.sales_comps} priceKey="sold_price" dateKey="sold_date" priceLabel="Sold" />
            <CompTable title="Rental comps" rows={comps.rental_comps} priceKey="monthly_rent" priceLabel="Rent/mo" />
          </div>
          <div className="mt-4 text-[11px] text-[var(--ink-3)] italic">
            These comps are AI-generated estimates for orientation only. Verify with your local MLS, agent, or a paid comps source before underwriting.
          </div>
        </section>
      )}
      {!comps && report.comps_error && (
        <div className="banner-err mb-6">Couldn&apos;t generate comps: {report.comps_error}</div>
      )}

      {/* Footer / agent card */}
      <footer
        className="card border-2 print:break-inside-avoid"
        style={{ borderColor: agent.brand_accent || "var(--ink)" }}
        data-testid="report-footer"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden"
              style={{ background: agent.brand_logo ? "#fff" : (agent.brand_accent || "var(--ink)"), color: "#fff", padding: agent.brand_logo ? 4 : 0 }}
            >
              {agent.brand_logo ? (
                <Img src={agent.brand_logo} alt="Brand logo" className="max-w-full max-h-full object-contain" data-testid="report-brand-logo" />
              ) : (
                <Building2 size={20} />
              )}
            </div>
            <div>
              <div className="font-display font-extrabold text-lg leading-tight" data-testid="report-brand-name">
                {agent.brand_company || "iSpeedToClose"} · Property Analysis
              </div>
              <div className="text-xs text-[var(--ink-2)]">Generated {report.generated_at ? new Date(report.generated_at).toLocaleString() : ""}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Prepared by</div>
            <div className="font-semibold mt-1">{agent.name}</div>
            {agent.email && <div className="text-sm text-[var(--ink-2)]"><a href={`mailto:${agent.email}`} className="underline">{agent.email}</a></div>}
          </div>
        </div>
      </footer>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          @page { size: Letter; margin: 0.5in; }
          body, .App { background: #fff !important; }
          .sidebar, .topbar { display: none !important; }
          .main-shell { margin-left: 0 !important; padding: 0 !important; }
          .card { box-shadow: none !important; break-inside: avoid; }
          a { color: inherit; text-decoration: none; }
        }
      `}</style>
    </article>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
      <div className="text-[10px] uppercase tracking-widest text-white/55 mb-1">{label}</div>
      <div className={"font-display font-bold text-lg " + (accent ? "text-[var(--primary)]" : "text-white")}>{value}</div>
    </div>
  );
}

function StrategyCard({ title, icon, metrics }) {
  return (
    <div className="card print:break-inside-avoid" data-testid={`strategy-${title.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center font-display font-extrabold">
          {icon}
        </div>
        <div>
          <h3 className="font-display font-bold text-lg leading-tight">{title}</h3>
        </div>
      </div>
      {!metrics ? (
        <div className="text-sm text-[var(--ink-2)] italic">Insufficient inputs (needs ARV).</div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {metrics.map((m, i) => (
            <div key={i} className={(m.full ? "col-span-2 " : "") + "bg-[var(--bg)] rounded-md p-3"}>
              <div className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-semibold">{m.label}</div>
              <div className={"font-display font-bold mt-0.5 tabular-nums " + (m.full ? "text-base" : "text-lg") + (m.accent ? " text-[var(--primary)]" : "")}>
                {m.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListBlock({ icon: Icon, title, items, accent }) {
  const color = accent === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} style={{ color }} />
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      <ul className="space-y-2">
        {(items || []).map((s, i) => (
          <li key={i} className="text-[14px] leading-relaxed flex items-start gap-2">
            <span className="font-mono mt-0.5" style={{ color }}>›</span>
            <span>{s}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompTable({ title, rows, priceKey, dateKey, priceLabel }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{title}</div>
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[var(--ink-2)] font-bold bg-[var(--bg)]">
              <th className="text-left p-2.5">Address</th>
              <th className="text-center p-2.5">Bd / Ba</th>
              <th className="text-right p-2.5">SqFt</th>
              <th className="text-right p-2.5">{priceLabel}</th>
              {dateKey && <th className="text-right p-2.5">Date</th>}
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r, i) => (
              <tr key={i} className="border-t border-[var(--line)]">
                <td className="p-2.5">
                  <div className="font-semibold leading-tight">{r.address}</div>
                  {r.distance_mi != null && <div className="text-[11px] text-[var(--ink-2)]">{r.distance_mi} mi away</div>}
                </td>
                <td className="text-center p-2.5 tabular-nums">{r.beds}/{r.baths}</td>
                <td className="text-right p-2.5 tabular-nums">{r.sqft ? Number(r.sqft).toLocaleString() : "—"}</td>
                <td className="text-right p-2.5 font-mono font-semibold tabular-nums">{fmtMoney(r[priceKey])}</td>
                {dateKey && <td className="text-right p-2.5 text-[12px] text-[var(--ink-2)]">{r[dateKey]}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Charts ─────────────────────────────────────────────────────────── */
const CH_INK = "#0F172A";
const CH_GREEN = "#10B981";
const CH_BLUE = "#3B82F6";
const CH_ORANGE = "#F59E0B";
const CH_RED = "#E76F51";
const CH_INK_2 = "#475569";
const CH_LINE = "#E2E8F0";

function ChartsSection({ lead, metrics, comps, satelliteUrl }) {
  const captions = comps?.chart_captions || {};
  // Strategy comparison data
  const stratData = [];
  if (metrics.wholesale?.projected_profit != null)
    stratData.push({ name: "Wholesale", profit: Math.round(metrics.wholesale.projected_profit), ret: Math.round(metrics.wholesale.roi_percent || 0), kind: "$" });
  if (metrics.flip?.projected_profit != null)
    stratData.push({ name: "Fix & Flip", profit: Math.round(metrics.flip.projected_profit), ret: Math.round(metrics.flip.roi_percent || 0), kind: "$" });
  if (metrics.brrrr) {
    // BRRRR "profit" framed as 1-yr cash flow + cash pulled out
    const yr1 = Math.round((metrics.brrrr.monthly_cash_flow || 0) * 12 + (metrics.brrrr.cash_pulled_out || 0));
    const coc = metrics.brrrr.cash_on_cash_pct === "Infinite" ? 100 : Math.round(metrics.brrrr.cash_on_cash_pct || 0);
    stratData.push({ name: "BRRRR (1yr)", profit: yr1, ret: coc, kind: "$" });
  }
  if (metrics.rental) {
    const yr1 = Math.round((metrics.rental.monthly_cash_flow || 0) * 12);
    stratData.push({ name: "Rental (1yr)", profit: yr1, ret: Math.round(metrics.rental.cash_on_cash_pct || 0), kind: "$" });
  }

  // 5-year rental projection (cumulative cash flow + equity appreciation)
  const projection = buildRentalProjection(metrics.rental, lead);

  // Sales comps scatter
  const compsSales = (comps?.sales_comps || []).map((c, i) => ({
    address: c.address, sqft: Number(c.sqft) || 0, price: Number(c.sold_price) || 0, idx: i + 1,
  }));
  const subjectPoint = {
    address: lead.property_address,
    sqft: Number(lead.sqft) || 0,
    price: Number(lead.estimated_arv) || 0,
  };
  const showCompsChart = compsSales.length > 0 && compsSales.some((c) => c.sqft && c.price);

  if (stratData.length === 0 && !projection && !showCompsChart && !satelliteUrl) return null;

  return (
    <section className="grid lg:grid-cols-2 gap-5 mb-6">
      {/* Location & aerial view (Google Static Maps w/ comp pins) */}
      {satelliteUrl && (
        <div className="card lg:col-span-2 print:break-inside-avoid" data-testid="aerial-view-card">
          <div className="label-eyebrow flex items-center gap-1.5 mb-1"><MapPin size={11} /> Location &amp; aerial view</div>
          <h3 className="font-display font-bold text-xl mb-1">Subject vs. nearby comps, from above</h3>
          <p className="text-xs text-[var(--ink-2)] mb-3">
            <span className="inline-flex items-center gap-1 mr-3"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#EF4444" }} /> Subject</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#F59E0B" }} /> Comps (1–5, ranked by proximity)</span>
          </p>
          <div className="rounded-xl overflow-hidden border border-[var(--line)]">
            <img
              src={satelliteUrl}
              alt="Aerial view"
              className="w-full block"
              style={{ aspectRatio: "16/10", objectFit: "cover" }}
              data-testid="aerial-view-img"
            />
          </div>
        </div>
      )}

      {/* Strategy comparison */}
      {stratData.length > 1 && (
        <div className="card lg:col-span-2 print:break-inside-avoid" data-testid="chart-strategy-comparison">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="label-eyebrow flex items-center gap-1.5"><BarChart3 size={11} /> Strategy comparison</div>
              <h3 className="font-display font-bold text-xl mt-1">Which exit yields the most?</h3>
            </div>
            <div className="text-xs text-[var(--ink-2)] flex items-center gap-3">
              <Legend2 color={CH_INK} label="Profit / Year-1 ($)" />
              <Legend2 color={CH_GREEN} label="Return (%)" />
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={stratData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CH_LINE} />
                <XAxis dataKey="name" tick={{ fill: CH_INK_2, fontSize: 12 }} />
                <YAxis yAxisId="left" tick={{ fill: CH_INK_2, fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}K`} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: CH_INK_2, fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: CH_INK, border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  formatter={(v, n) => n === "profit" ? [fmtMoney(v), "Profit / 1-yr"] : [`${v}%`, "Return"]}
                />
                <Bar yAxisId="left" dataKey="profit" radius={[6, 6, 0, 0]}>
                  {stratData.map((d, i) => (
                    <Cell key={i} fill={d.profit > 0 ? CH_INK : CH_RED} />
                  ))}
                </Bar>
                <Bar yAxisId="right" dataKey="ret" radius={[6, 6, 0, 0]} fill={CH_GREEN} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {captions.strategy && <ChartCaption text={captions.strategy} />}
        </div>
      )}

      {/* 5-year wealth projection (rental) */}
      {projection && (
        <div className="card print:break-inside-avoid" data-testid="chart-rental-projection">
          <div className="label-eyebrow flex items-center gap-1.5 mb-1"><LineIcon size={11} /> 5-Year wealth projection · Rental</div>
          <h3 className="font-display font-bold text-xl mb-1">Cash flow + equity over time</h3>
          <p className="text-xs text-[var(--ink-2)] mb-3">Assumes 3% appreciation, 3% rent growth, 20% down. Equity = appreciation only.</p>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={projection} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="g-cf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CH_GREEN} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={CH_GREEN} stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="g-eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CH_BLUE} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={CH_BLUE} stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={CH_LINE} />
                <XAxis dataKey="year" tick={{ fill: CH_INK_2, fontSize: 11 }} tickFormatter={(v) => `Yr ${v}`} />
                <YAxis tick={{ fill: CH_INK_2, fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}K`} />
                <Tooltip
                  contentStyle={{ background: CH_INK, border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  formatter={(v, n) => [fmtMoney(v), n === "cum_cash_flow" ? "Cumulative cash flow" : "Equity (appreciation)"]}
                  labelFormatter={(l) => `Year ${l}`}
                />
                <Area type="monotone" dataKey="equity" stroke={CH_BLUE} fill="url(#g-eq)" strokeWidth={2} />
                <Area type="monotone" dataKey="cum_cash_flow" stroke={CH_GREEN} fill="url(#g-cf)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-[var(--ink-2)]">
            <Legend2 color={CH_GREEN} label="Cumulative cash flow" />
            <Legend2 color={CH_BLUE} label="Equity gain" />
          </div>
          {captions.projection && <ChartCaption text={captions.projection} />}
        </div>
      )}

      {/* Comps scatter */}
      {showCompsChart && (
        <div className="card print:break-inside-avoid" data-testid="chart-comps-scatter">
          <div className="label-eyebrow flex items-center gap-1.5 mb-1"><Target size={11} /> Sales comps map</div>
          <h3 className="font-display font-bold text-xl mb-1">Subject vs nearby comps</h3>
          <p className="text-xs text-[var(--ink-2)] mb-3">Each dot is a recent sale. The green star is the subject at estimated ARV.</p>
          <div className="h-64">
            <ResponsiveContainer>
              <ScatterChart margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CH_LINE} />
                <XAxis type="number" dataKey="sqft" name="SqFt" tick={{ fill: CH_INK_2, fontSize: 11 }} tickFormatter={(v) => `${v.toLocaleString()}`} label={{ value: "SqFt", position: "insideBottom", offset: -5, fill: CH_INK_2, fontSize: 11 }} />
                <YAxis type="number" dataKey="price" name="Price" tick={{ fill: CH_INK_2, fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}K`} />
                <ZAxis range={[100, 200]} />
                <Tooltip
                  contentStyle={{ background: CH_INK, border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  cursor={{ strokeDasharray: "3 3" }}
                  formatter={(v, n) => n === "price" ? [fmtMoney(v), "Price"] : [`${v.toLocaleString()} sqft`, "SqFt"]}
                  labelFormatter={() => ""}
                  itemSorter={(it) => -it.value}
                />
                <Scatter data={compsSales} fill={CH_ORANGE} />
                {subjectPoint.sqft > 0 && subjectPoint.price > 0 && (
                  <ReferenceDot x={subjectPoint.sqft} y={subjectPoint.price} r={8} fill={CH_GREEN} stroke="#fff" strokeWidth={2} label={{ value: "Subject", position: "top", fill: CH_GREEN, fontSize: 11, fontWeight: 700 }} />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          {captions.comps && <ChartCaption text={captions.comps} />}
        </div>
      )}
    </section>
  );
}

function ChartCaption({ text }) {
  return (
    <div className="mt-3 pt-3 border-t border-dashed border-[var(--line)] flex items-start gap-2 text-[13px] leading-relaxed">
      <Sparkles size={13} className="mt-0.5 text-[var(--primary)] shrink-0" />
      <span className="text-[var(--ink-2)]"><em className="not-italic font-semibold text-[var(--ink)]">AI read:</em> {text}</span>
    </div>
  );
}

function Legend2({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
      <span>{label}</span>
    </span>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}


function buildRentalProjection(rental, lead) {
  if (!rental) return null;
  const monthlyRent = Number(rental.estimated_monthly_rent) || 0;
  const monthlyExp = Number(rental.estimated_monthly_expenses) || 0;
  const monthlyPi = Number(rental.monthly_pi) || 0;
  const purchase = Number(rental.purchase_price) || Number(lead.asking_price) || 0;
  if (!purchase) return null;
  const APPRECIATION = 0.03;
  const RENT_GROWTH = 0.03;
  let cum_cash_flow = 0;
  const out = [{ year: 0, cum_cash_flow: 0, equity: 0 }];
  for (let y = 1; y <= 5; y++) {
    const grownRent = monthlyRent * Math.pow(1 + RENT_GROWTH, y - 1);
    const cf = (grownRent - monthlyExp - monthlyPi) * 12;
    cum_cash_flow += cf;
    const equity = purchase * (Math.pow(1 + APPRECIATION, y) - 1);
    out.push({ year: y, cum_cash_flow: Math.round(cum_cash_flow), equity: Math.round(equity) });
  }
  return out;
}

